/* eslint complexity: off */
import type { StateMapping, UiState } from 'instantsearch.js';
import { BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history';
import { getCategoryNameFromSlug } from '@utils/algolia/getCategoryNameFromSlug';
import { slugify } from '@utils/url';
import { getStoreDictionary } from '@utils/store/storeIdDictionary';
import { getCategorySlugsFromUrl } from '@utils/algolia/getCategorySlugsFromUrl';
import { BRANDS, MARQUES } from '@utils/plp/constants';
import { getCategoryPageId } from '@utils/algolia/getCategoryPageId';
import { removeKnownFilterFromCategoryPath } from '@utils/algolia/urlIsKnownFilter';
import { getIndex } from '@utils/algolia/analytics';
import { mergeQueryStrings } from '@utils/url';
import Cookies from 'js-cookie';
import {
  isCollectionPlp,
  isBrandPlp,
  isTagsPlp,
  parseAlgoliaUrl,
  localizedProductsSlug,
  isCanadianPlp,
} from '@utils/algolia/helpers';
import { AlgoliaFacets } from './knownFacetNames';
import {
  getPriceAttribute,
  isEffectivePriceAttribute,
} from '@utils/algolia/priceContext';

// custom: category, gender, brand, price, colour, rating, storeinventory
type RouteState = {
  locale?: string;
  q?: UiState[string]['query'];
  page?: string;
  brand?: string[];
  category?: string;
  reviewScore?: string[];
  price?: string;
  sortBy?: string;
  shoeWidth?: string[];
  gender?: string[];
  colour?: string[];
  sizeClothingMens?: string[];
  sizeClothingWomens?: string[];
  sizeClothingKids?: string[];
  clothingSizeKids?: string[];
  clothingSizeWomens?: string[];
  clothingSizeMens?: string[];
  promotions?: string[];
  shopCanadian?: string[];
  discount?: string[];
  storeInventory?: string[];
  configure?: { filters?: string | undefined };
  [key: string]: any;
};

type VanityLabels = {
  [label: string]: string;
};

const vanityLabels: VanityLabels = {
  "size.US Men's": 'size.us-mens',
  "size.US Women's": 'size.us-womens',
  'size.Mondopoint': 'size.mondopoint',
  'size.EU': 'size.eu',
  'size.Infants': 'size.infants',
  'size.Kids': 'size.kids',
  'size.Youth': 'size.youth',
  'size.US Hommes': 'size.us-hommes',
  'size.US Femmes': 'size.us-femmes',
  'size.Jeunes': 'size.jeunes',
  'size.Enfants': 'size.enfants',
  'size.Bébés': 'size.bebes',
};

const getVanityLabelKey = (key: string): string => {
  const facetKey =
    Object.keys(vanityLabels).find((label) => vanityLabels[label] === key) ||
    key;

  return facetKey;
};

const sanitizeSlug = (slug: string) => {
  return slug.replace(/'/g, "\\'");
};

const routeStateDefaultValues: RouteState = {
  locale: 'en',
  q: '',
  page: '1',
  brand: undefined,
  category: '',
  reviewScore: undefined,
  price: undefined,
  sortBy: getIndex('en'),
  gender: undefined,
  colour: undefined,
  sizeClothingMens: undefined,
  sizeClothingWomens: undefined,
  sizeClothingKids: undefined,
  clothingSizeKids: undefined,
  clothingSizeWomens: undefined,
  clothingSizeMens: undefined,
  promotions: undefined,
  shopCanadian: undefined,
  discount: undefined,
  storeInventory: undefined,
  configure: undefined,
  shoeWidth: undefined,
};

const algoliaDebug = Cookies.get('algoliaDebug');

const getStateMapping = ({
  indexName,
  locale,
  serverUrl,
  priceAttribute,
}: {
  indexName: string;
  locale: string;
  serverUrl: string;
  priceAttribute: string;
}) => ({
  stateToRoute(uiState: UiState): RouteState {
    const indexUiState = uiState[indexName];

    let categoryUrl = '';
    if (
      indexUiState.hierarchicalMenu &&
      indexUiState.hierarchicalMenu['categories.lvl0']
    ) {
      categoryUrl = indexUiState.hierarchicalMenu['categories.lvl0']
        .map(slugify)
        .join('/');
    }

    const sizeFacets = Object.keys(indexUiState.refinementList ?? {})
      .filter((key) => key.startsWith('size.'))
      .reduce((acc, key) => {
        const vanityLabel = vanityLabels[key] ?? key;
        acc[vanityLabel] = indexUiState.refinementList?.[key] ?? [];
        return acc;
      }, {} as Record<string, string[]>);

    const facetFacets = Object.keys(indexUiState.refinementList ?? {})
      .filter((key) => key.startsWith('facets.'))
      .reduce((acc, key) => {
        acc[key] = indexUiState.refinementList?.[key] ?? [];
        return acc;
      }, {} as Record<string, string[]>);

    const mapRefinementList = (key: string) =>
      indexUiState.refinementList && indexUiState.refinementList[key];

    const mapRefinementListWithReplace = (
      key: string,
      search: string,
      replace: string
    ) =>
      indexUiState.refinementList &&
      indexUiState.refinementList[key]?.map((str: string) =>
        str.replaceAll(search, replace)
      );

    return {
      q: indexUiState.query,
      configure: indexUiState.configure,
      locale,
      page: (indexUiState.page && String(indexUiState.page)) || undefined,
      brand: mapRefinementList(AlgoliaFacets.Brand),
      category: categoryUrl,
      reviewScore: mapRefinementList(AlgoliaFacets.ReviewScoreFlattened),
      sortBy: indexUiState.sortBy,
      gender: mapRefinementList(AlgoliaFacets.Gender),
      colour: mapRefinementList(AlgoliaFacets.ColourVariants),
      price: indexUiState.range
        ? Object.entries(indexUiState.range).find(([key]) =>
            isEffectivePriceAttribute(key)
          )?.[1]
        : undefined,
      sizeClothingMens: mapRefinementList(AlgoliaFacets.SizeClothingMens),
      sizeClothingWomens: mapRefinementList(AlgoliaFacets.SizeClothingWomens),
      sizeClothingKids: mapRefinementList(AlgoliaFacets.SizeClothingKids),
      clothingSizeKids: mapRefinementListWithReplace(
        AlgoliaFacets.ClothingSizeKids,
        ' ',
        '+'
      ),
      clothingSizeWomens: mapRefinementListWithReplace(
        AlgoliaFacets.ClothingSizeWomens,
        ' ',
        '+'
      ),
      clothingSizeMens: mapRefinementListWithReplace(
        AlgoliaFacets.ClothingSizeMens,
        ' ',
        '+'
      ),
      promotions: mapRefinementList(AlgoliaFacets.Promotions),
      shopCanadian:
        indexUiState.refinementList && indexUiState.refinementList.shopCanadian,
      discount: mapRefinementListWithReplace(AlgoliaFacets.Discount, '%', ''),
      storeInventory: mapRefinementList(AlgoliaFacets.StoreInventory),
      shoeWidth: mapRefinementList(AlgoliaFacets.ShoeWidth),
      ...facetFacets,
      ...sizeFacets,
    };
  },

  routeToState(routeState: RouteState): UiState {
    const hierarchicalMenu: { [key: string]: string[] } = {};

    const mapRouteStateWithReplace = (
      key: string,
      search: string,
      replace: string
    ) =>
      routeState &&
      routeState[key]?.map((str: string) => str.replaceAll(search, replace));

    const categoryPageId = getCategoryPageId(serverUrl, locale);
    if (routeState.category) {
      const splitChar = routeState.category.includes('>') ? ' > ' : '/';

      hierarchicalMenu['categories.lvl0'] = routeState.category
        .split(splitChar)
        .map((slug) => {
          return getCategoryNameFromSlug(slug, locale);
        });
    }

    const refinementList: { [key: string]: string[] } = {};
    if (routeState.brand) {
      refinementList.brand = routeState.brand;
    }

    if (routeState.gender) {
      refinementList.gender = routeState.gender;
    }

    if (routeState.colour) {
      refinementList.colour_variants = routeState.colour;
    }

    if (routeState.reviewScore) {
      refinementList.reviewScoreFlattened = routeState.reviewScore;
    }

    if (routeState.sizeClothingMens) {
      refinementList.sizeClothingMens = routeState.sizeClothingMens;
    }

    if (routeState.sizeClothingWomens) {
      refinementList.sizeClothingWomens = routeState.sizeClothingWomens;
    }

    if (routeState.sizeClothingKids) {
      refinementList.sizeClothingKids = routeState.sizeClothingKids;
    }

    if (routeState.clothingSizeKids) {
      refinementList.clothingSizeKids = mapRouteStateWithReplace(
        AlgoliaFacets.ClothingSizeKids,
        '+',
        ' '
      );
    }

    if (routeState.clothingSizeWomens) {
      refinementList.clothingSizeWomens = mapRouteStateWithReplace(
        AlgoliaFacets.ClothingSizeWomens,
        '+',
        ' '
      );
    }

    if (routeState.clothingSizeMens) {
      refinementList.clothingSizeMens = mapRouteStateWithReplace(
        AlgoliaFacets.ClothingSizeMens,
        '+',
        ' '
      );
    }

    if (routeState.promotions) {
      refinementList.promotions = routeState.promotions;
    }

    if (routeState.shopCanadian) {
      refinementList.shopCanadian = routeState.shopCanadian;
    }

    if (routeState.discount) {
      refinementList.discount = routeState.discount;
    }

    if (routeState.storeInventory) {
      refinementList.storeInventory = routeState.storeInventory;
    }

    if (routeState.shoeWidth) {
      refinementList.shoeWidth = routeState.shoeWidth;
    }

    Object.keys(routeState).forEach((key) => {
      if (
        key.startsWith('size.') ||
        key.startsWith('clothingSize.') ||
        key.startsWith('facets.')
      ) {
        refinementList[key] = routeState[key];
      }
    });

    // The URL price value is group-agnostic (`10:50`), but the range connector
    // keys refinements by attribute. `priceAttribute` is resolved from the
    // customer's pricing context (SSR-seeded, so it is correct on first render);
    // a client-side login/logout flip is reconciled by `PriceContextSync`.
    const range: { [key: string]: string } = {};
    if (routeState.price && routeState.price !== 'undefined') {
      range[priceAttribute] = routeState.price;
    }

    const facetFilter = categoryPageId
      ? `categoryPageId:'${sanitizeSlug(categoryPageId)}'`
      : '';

    return {
      [indexName]: {
        query: routeState.q,
        page: Number(routeState.page),
        hierarchicalMenu,
        refinementList,
        sortBy: routeState.sortBy,
        range,
        configure: {
          filters: facetFilter,
        },
      },
    };
  },
});

export type GetRouting = ReturnType<typeof getRouting>;

const getRouting = (
  indexName: string,
  serverUrl: string,
  locale: string,
  // Effective-price attribute for the active pricing context. Used to key the
  // range refinement parsed from the URL; defaults to guest for callers without
  // a resolved customer group.
  priceAttribute: string = getPriceAttribute('guest')
): {
  routerOptions: BrowserHistoryArgs<RouteState>;
  stateMapping: StateMapping;
} => ({
  routerOptions: {
    cleanUrlOnDispose: false,
    getLocation() {
      return typeof window !== 'undefined'
        ? window.location
        : (new URL(serverUrl) as unknown as Location);
    },

    createURL({ qsModule, routeState, location }) {
      const { pathname, hash } = location;
      const pageSlug = pathname.includes('/search') ? 'search' : 'products';
      const isSearchPage = pageSlug === 'search';
      const { activeSPU } = getStoreDictionary(locale);
      const productsSlug = localizedProductsSlug(locale).toLowerCase();
      const localizedBrandSlug = locale === 'fr' ? MARQUES : BRANDS;
      const urlOrigin = location.origin
        .replace('bc-prod.', 'www.')
        .replace('bc-uat.', 'uat.')
        .replace('https://mec.ca', 'https://www.mec.ca');

      const isCollectionUrl = isCollectionPlp(location.pathname);
      const isCanadianUrl = isCanadianPlp(location.pathname, locale);

      const mapRouteStateEncodeWithReplace = (
        key: string,
        search: string,
        replace: string
      ) =>
        routeState &&
        routeState[key]?.map((str: string) =>
          encodeURIComponent(str).replaceAll(search, replace)
        );

      const {
        categorySlug: categorySlugParts,
        categoryFilters: categoryFilterParts,
      } = getCategorySlugsFromUrl(pathname, locale);

      const categoryPath = routeState.category;

      const categorySlug = categorySlugParts
        ? `${productsSlug}/${categorySlugParts.join('/')}`
        : productsSlug;

      const { brandIdentifier = '', brandCategoryFilter = '' } =
        parseAlgoliaUrl(location.pathname, locale);

      const queryParameters: Partial<RouteState> = {};

      // don't have it, don't set it
      const categoryIsSet =
        routeState.category &&
        routeState.category !== routeStateDefaultValues.category;

      // all products page
      const categoryIsProducts = routeState.category === productsSlug;
      const categoryIsCurrentPath = categoryPath === categorySlug;

      const addCategoryAsQueryParam =
        (isSearchPage || isCollectionUrl || isCanadianUrl) &&
        categoryIsSet &&
        !categoryIsProducts &&
        !categoryIsCurrentPath &&
        !brandCategoryFilter;

      if (routeState.q && routeState.q !== routeStateDefaultValues.q) {
        queryParameters.q = routeState.q;
      }
      if (routeState.page && routeState.page !== routeStateDefaultValues.page) {
        queryParameters.page = routeState.page;
      }
      if (addCategoryAsQueryParam && routeState.category) {
        queryParameters.category = routeState.category
          ?.split('/')
          .slice(1)
          .join('|');
      }

      // append the brand param, except /brands/x/y/z pages
      if (
        routeState.brand &&
        routeState.brand !== routeStateDefaultValues.brand &&
        !brandIdentifier
      ) {
        queryParameters.brand = routeState.brand.map(encodeURIComponent);
      }
      if (
        routeState.gender &&
        routeState.gender !== routeStateDefaultValues.gender
      ) {
        queryParameters.gender = routeState.gender.map(encodeURIComponent);
      }
      if (
        routeState.colour &&
        routeState.colour !== routeStateDefaultValues.colour
      ) {
        queryParameters.colour = routeState.colour.map(encodeURIComponent);
      }
      if (
        routeState.reviewScore &&
        routeState.reviewScore !== routeStateDefaultValues.reviewScore
      ) {
        queryParameters.reviewScore = routeState.reviewScore;
      }
      if (
        routeState.price &&
        routeState.price !== routeStateDefaultValues.price &&
        routeState.price !== 'undefined'
      ) {
        queryParameters.price = routeState.price;
      }
      if (
        routeState.sortBy &&
        routeState.sortBy !== routeStateDefaultValues.sortBy
      ) {
        queryParameters.sortBy = routeState.sortBy;
      }
      if (
        routeState.sizeClothingMens &&
        routeState.sizeClothingMens !== routeStateDefaultValues.sizeClothingMens
      ) {
        queryParameters.sizeClothingMens =
          routeState.sizeClothingMens.map(encodeURIComponent);
      }
      if (
        routeState.sizeClothingWomens &&
        routeState.sizeClothingWomens !==
          routeStateDefaultValues.sizeClothingWomens
      ) {
        queryParameters.sizeClothingWomens =
          routeState.sizeClothingWomens.map(encodeURIComponent);
      }
      if (
        routeState.sizeClothingKids &&
        routeState.sizeClothingKids !== routeStateDefaultValues.sizeClothingKids
      ) {
        queryParameters.sizeClothingKids =
          routeState.sizeClothingKids.map(encodeURIComponent);
      }
      if (
        routeState.clothingSizeKids &&
        routeState.clothingSizeKids !== routeStateDefaultValues.clothingSizeKids
      ) {
        queryParameters.clothingSizeKids = mapRouteStateEncodeWithReplace(
          AlgoliaFacets.ClothingSizeKids,
          '%2B',
          '+'
        );
      }
      if (
        routeState.clothingSizeWomens &&
        routeState.clothingSizeWomens !==
          routeStateDefaultValues.clothingSizeWomens
      ) {
        queryParameters.clothingSizeWomens = mapRouteStateEncodeWithReplace(
          AlgoliaFacets.ClothingSizeWomens,
          '%2B',
          '+'
        );
      }
      if (
        routeState.clothingSizeMens &&
        routeState.clothingSizeMens !== routeStateDefaultValues.clothingSizeMens
      ) {
        queryParameters.clothingSizeMens = mapRouteStateEncodeWithReplace(
          AlgoliaFacets.ClothingSizeMens,
          '%2B',
          '+'
        );
      }
      if (
        routeState.promotions &&
        routeState.promotions !== routeStateDefaultValues.promotions
      ) {
        queryParameters.promotions =
          routeState.promotions.map(encodeURIComponent);
      }
      if (
        routeState.shopCanadian &&
        routeState.shopCanadian !== routeStateDefaultValues.shopCanadian
      ) {
        queryParameters.shopCanadian =
          routeState.shopCanadian.map(encodeURIComponent);
      }
      if (
        routeState.discount &&
        routeState.discount !== routeStateDefaultValues.discount
      ) {
        queryParameters.discount = routeState.discount;
      }
      if (
        routeState.storeInventory &&
        routeState.storeInventory !== routeStateDefaultValues.storeInventory
      ) {
        queryParameters.storeInventory = routeState.storeInventory.map(
          (store) => {
            return encodeURIComponent(activeSPU[store] || store);
          }
        );
      }
      if (
        routeState.shoeWidth &&
        routeState.shoeWidth !== routeStateDefaultValues.shoeWidth
      ) {
        queryParameters.shoeWidth =
          routeState.shoeWidth.map(encodeURIComponent);
      }

      Object.keys(routeState).forEach((key) => {
        const isSizeOrFacet =
          key.startsWith('size.') || key.startsWith('facets.');
        if (
          isSizeOrFacet &&
          routeState[key] &&
          routeState[key] !== routeStateDefaultValues[key]
        ) {
          queryParameters[key] = routeState[key].map(encodeURIComponent);
        }
      });

      const queryString = qsModule.stringify(queryParameters, {
        addQueryPrefix: true,
        arrayFormat: 'comma',
        encode: false,
      });

      const categoryFilterPath = categoryFilterParts?.length
        ? `/${categoryFilterParts.join('/')}`
        : ``;

      if (isSearchPage) {
        const qs = mergeQueryStrings(queryString, location.search);
        return `${urlOrigin}/${locale}/search${qs}`;
      }

      if (brandIdentifier) {
        const brandCategoryPath = categoryPath?.split('/').slice(1).join('/');
        return `${urlOrigin}/${locale}/${productsSlug}/${localizedBrandSlug}/${brandIdentifier}/${brandCategoryPath}${categoryFilterPath}${queryString}${hash}`;
      }

      if (isCollectionUrl || isCanadianUrl) {
        return `${urlOrigin}${location.pathname}${queryString}${hash}`;
      }

      return `${urlOrigin}/${locale}/${categoryPath}${categoryFilterPath}${queryString}${hash}`;
    },

    parseURL({ qsModule, location }) {
      const productsSlug = localizedProductsSlug(locale);

      if (algoliaDebug) {
        console.log('algolia: parseURL serverUrl', serverUrl);
        console.log('algolia: parseURL', location);
      }

      const isBrandUrl = isBrandPlp(location.pathname);
      const isCollectionUrl = isCollectionPlp(location.pathname);
      const isCanadianUrl = isCanadianPlp(location.pathname, locale);
      const isTagsUrl = isTagsPlp(location.pathname);
      const pageSlug = location.pathname.includes('/search')
        ? 'search'
        : locale === 'fr'
        ? 'produits'
        : 'products';
      const isSearchPage = pageSlug === 'search';
      const pageShouldUseCategoryParam =
        isSearchPage || isCollectionUrl || isTagsUrl || isCanadianUrl;

      const { categorySlug: categorySlugParts } = getCategorySlugsFromUrl(
        location.pathname,
        locale
      );

      if (categorySlugParts) {
        categorySlugParts.unshift(productsSlug);
      }

      const queryParameters = qsModule.parse(location.search.slice(1), {
        comma: true,
      });

      const {
        query = queryParameters?.q || '',
        page = 1,
        brand = [],
        price,
        sortBy,
        reviewScore = [],
        gender = [],
        colour = [],
        category,
        sizeClothingMens = [],
        sizeClothingWomens = [],
        sizeClothingKids = [],
        clothingSizeKids = [],
        clothingSizeWomens = [],
        clothingSizeMens = [],
        promotions = [],
        discount = [],
        storeInventory = [],
        shoeWidth = [],
        shopCanadian = [],
      } = queryParameters;

      // `qs` does not return an array when there's a single value.

      const nonArrayCallbackDefault = (value: string): string[] =>
        value.split(',').filter(Boolean);
      type ExtractArrayTypes<T> = T extends (infer U)[] ? T : never;
      const extractToArray = (
        value: (typeof queryParameters)[string],
        arrayCallback?: (
          value: ExtractArrayTypes<(typeof queryParameters)[string]>
        ) => string[],
        nonArrayCallback = nonArrayCallbackDefault
      ): string[] => {
        return Array.isArray(value)
          ? arrayCallback
            ? arrayCallback(value)
            : (value as string[])
          : typeof value === 'string'
          ? nonArrayCallback(value)
          : [];
      };

      const allBrands = extractToArray(brand);

      const allGenders = extractToArray(gender);

      const allColours = extractToArray(colour);

      const allReviewScores = extractToArray(reviewScore);

      const allMensClothingSizes = extractToArray(sizeClothingMens);

      const allWomensClothingSizes = extractToArray(sizeClothingWomens);

      const allKidsClothingSizes = extractToArray(sizeClothingKids);

      const allShopCanadian = extractToArray(shopCanadian);

      const allClothingSizeKids = extractToArray(clothingSizeKids);

      const allClothingSizeWomens = extractToArray(clothingSizeWomens);

      const allClothingSizeMens = extractToArray(clothingSizeMens);

      const allPromotions = extractToArray(promotions);

      const allDiscounts = extractToArray(
        discount,
        (values) => (values ?? []).map((str) => str + '%'),
        (value) =>
          value
            .split(',')
            .filter(Boolean)
            .map((str) => str + '%')
      );

      const allStoreLabels = extractToArray(storeInventory);

      const allShoeWidth = extractToArray(shoeWidth);

      let categoryFilter = productsSlug;

      if (pageShouldUseCategoryParam && category) {
        const searchCategoryPath = (category as string).split('|').join('/');
        categoryFilter = `${productsSlug}/${searchCategoryPath}`;
      }

      let isFilteredBrand = false;
      if (isBrandUrl) {
        const { brandIdentifier, brandCategoryFilter } = parseAlgoliaUrl(
          location.pathname,
          locale
        );

        allBrands.push(getCategoryNameFromSlug(brandIdentifier, locale));

        if (brandCategoryFilter) {
          isFilteredBrand = Boolean(brandCategoryFilter);
          const brandCategoryUrl = removeKnownFilterFromCategoryPath(
            brandCategoryFilter,
            locale,
            '/'
          );
          const brandCategoryPath = brandCategoryUrl
            .split('/')
            .map((slug: string) => {
              return getCategoryNameFromSlug(slug, locale);
            })
            .join(' > ');
          categoryFilter = brandCategoryPath
            ? `${productsSlug} > ${brandCategoryPath}`
            : productsSlug;
        }
      }

      const categoryUrl =
        categorySlugParts?.length &&
        !isFilteredBrand &&
        !pageShouldUseCategoryParam
          ? categorySlugParts.join('/')
          : categoryFilter;

      const categoryPath = categoryUrl
        ?.split('/')
        .map((slug: string) => {
          return getCategoryNameFromSlug(slug, locale);
        })
        .join(' > ');

      let forceBaseCategory = isBrandUrl && !isFilteredBrand;
      if (isFilteredBrand) {
        forceBaseCategory = false;
      }

      const { activeSPU } = getStoreDictionary(locale);
      const allStoreIds = allStoreLabels.map((store) => {
        const storeName = decodeURIComponent(store);
        const storeId = Object.keys(activeSPU).find(
          (key) => activeSPU[key] === storeName
        );
        return storeId;
      });

      const dynamicSizeFacets: { [key: string]: any } = {};
      Object.keys(queryParameters).forEach((key) => {
        const keyIsSizeOrFacet =
          key.startsWith('size.') || key.startsWith('facets.');
        if (keyIsSizeOrFacet && queryParameters[key]) {
          const facetKey = getVanityLabelKey(key);
          dynamicSizeFacets[facetKey] = Array.isArray(queryParameters[key])
            ? (queryParameters[key] as string[]).map(decodeURIComponent)
            : (queryParameters[key] as string)
                .split(',')
                .map(decodeURIComponent);
        }
      });

      return {
        q: query as UiState[string]['query'],
        locale,
        pageSlug,
        page: page as string,
        category: forceBaseCategory ? productsSlug : categoryPath,
        brand: allBrands.map(decodeURIComponent),
        reviewScore: allReviewScores.map(decodeURIComponent),
        price: decodeURIComponent(price as string),
        sortBy: sortBy as string,
        gender: allGenders.map(decodeURIComponent),
        colour: allColours.map(decodeURIComponent),
        sizeClothingMens: allMensClothingSizes.map(decodeURIComponent),
        sizeClothingWomens: allWomensClothingSizes.map(decodeURIComponent),
        sizeClothingKids: allKidsClothingSizes.map(decodeURIComponent),
        clothingSizeKids: allClothingSizeKids.map(decodeURIComponent),
        clothingSizeWomens: allClothingSizeWomens.map(decodeURIComponent),
        clothingSizeMens: allClothingSizeMens.map(decodeURIComponent),
        promotions: allPromotions.map(decodeURIComponent),
        shopCanadian: allShopCanadian.map(decodeURIComponent),
        discount: allDiscounts,
        storeInventory: allStoreIds.filter(
          (id): id is string => id !== undefined
        ),
        shoeWidth: allShoeWidth.map(decodeURIComponent),
        ...dynamicSizeFacets,
      };
    },
    writeDelay: 400, // default is 400
  },

  stateMapping: getStateMapping({
    indexName,
    locale,
    serverUrl,
    priceAttribute,
  }),
});

export default getRouting;
